
import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { formatCurrency } from '@/components/borrowings/debt/utils';
import { TimeHorizon } from '../types';
import { ALMContract } from './types';
import { ChevronRight } from 'lucide-react';

interface ContractTableRowProps {
  contract: ALMContract;
  currentHorizons: TimeHorizon[];
  onClick: () => void;
}

const ContractTableRow: React.FC<ContractTableRowProps> = ({ 
  contract, 
  currentHorizons,
  onClick
}) => {
  // Helper function to map horizon IDs to cashflows in the contract
  const getCashflowForHorizon = (horizonId: string): number => {
    // Make sure we're looking up the correct key in the cashflows object
    return contract.cashflows[horizonId] || 0;
  };

  // Format the contract name to show a descriptive prefix based on the contract type code
  const getFormattedContractName = (name: string): string => {
    // Check if the name matches patterns like VL-001, PL-002, etc.
    const contractPatterns = {
      'VL-': 'Vehicle Loan ID: ',
      'PL-': 'Personal Loan ID: ',
      'SME-': 'SME Loan ID: ',
      'GL-': 'Gold Loan ID: '
    };
    
    for (const [prefix, displayPrefix] of Object.entries(contractPatterns)) {
      if (name.startsWith(prefix)) {
        // Extract just the numeric part for cleaner display
        const idPart = name.split('-')[1];
        return `${displayPrefix}${idPart}`;
      }
    }
    
    // If not matching any of our specific loan types, return the original name
    return name;
  };

  // Debug the contract and horizon data
  console.log("Contract being rendered:", contract.name);
  console.log("Contract cashflows:", contract.cashflows);
  console.log("Current horizons:", currentHorizons.map(h => h.id));

  return (
    <TableRow 
      className="cursor-pointer hover:bg-muted/60 transition-colors"
      onClick={onClick}
    >
      <TableCell className="font-medium pl-20">
        <div className="flex items-center">
          <span>{getFormattedContractName(contract.name)}</span>
          <ChevronRight className="h-4 w-4 ml-2 text-muted-foreground" />
        </div>
      </TableCell>
      
      {currentHorizons.map(horizon => (
        <TableCell key={horizon.id} className="text-right">
          {formatCurrency(getCashflowForHorizon(horizon.id))}
        </TableCell>
      ))}
    </TableRow>
  );
};

export default ContractTableRow;
